import { AddressAutocomplete } from "@components/address-autocomplete/address-autocomplete";
import { WorldMap } from "@components/world-map/world-map";
import { WorldMapHandle } from "@components/world-map/world-map-types";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseProjectProps } from "@custom-types/project-types";
import {
  CloseIcon,
  Dialog,
  IconButton,
  Text,
  TextField,
  useToast,
} from "@flat-ui";
import { SEARCH_DEBOUNCE_TIME } from "@hooks/use-debounced-search-text";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { FetchingStatus } from "@store/store-types";
import { GeocodedAddress, reverseGeocode } from "@utils/geocoding-utils";
import {
  parseCoordinatesString,
  stringifyCoordinates,
} from "@utils/string-utils";
import { useDebounce } from "@utils/time-utils";
import { DASH } from "@utils/ui-utils";
import { useMemo, useRef, useState } from "react";

interface Props extends BaseProjectProps {
  /** Callback function to close the dialog */
  onClose(): void;
}

/**
 * @returns A dialog that allows users to edit the location of a project.
 * The address field can be left empty.
 * The coordinates (position) field cannot be empty due to a backend requirement.
 */
export function ProjectLocationEditor({
  project,
  onClose,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const initialCoordinates = stringifyCoordinates(project.position);
  const [coordinates, setCoordinates] = useState<string>(initialCoordinates);
  const [coordinatesError, setCoordinatesError] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [geocodingAddressState, setGeocodingAddressState] =
    useState<FetchingStatus>("uninitialized");
  const [address, setAddress] = useState<string>(project.address);
  const mapRef = useRef<WorldMapHandle>(null);

  const debouncedCoordinates = useDebounce(coordinates, SEARCH_DEBOUNCE_TIME);
  // Debounce position changes before passing it to the map component
  // to avoid the map from flickering while the user is typing.
  // The debounced position needs to be memoized, otherwise any changes in
  // the parent component state would cause the map entities-layer to be recreated,
  // causing a visible flicker in the map.
  const debouncePosition = useMemo(() => {
    return parseCoordinatesString(debouncedCoordinates);
  }, [debouncedCoordinates]);

  /**
   * World map entities array with a single entity that represent the current GPS coordinates.
   * If the coordinates input is empty then the array is empty so that no marker is shown on the map.
   * The array is memoized to keep the same array reference and avoid a flickering in the map
   * any time that the array value is re-computed.
   */
  const entities = useMemo(() => {
    return debouncePosition ? [{ ...project, position: debouncePosition }] : [];
  }, [project, debouncePosition]);

  /**
   * Moves the map to the given coordinates if they are valid.
   * @param value The coordinates string to go to
   */
  function goToCoordinates(value: string): void {
    const parsedCoordinates = parseCoordinatesString(value);
    if (parsedCoordinates) {
      mapRef.current?.goTo(parsedCoordinates);
    }
  }

  /**
   * Handles updates to the address input field.
   * It sets the hasChanges flag to true if it's the first change, and it updates the address state.
   * @param newAddress The new address input value
   */
  function updateAddressField(newAddress: string): void {
    if (!hasChanges) {
      setHasChanges(true);
    }
    setAddress(newAddress);
  }

  /**
   * Handles updates to the coordinates input field.
   * If there was an error it resets it.
   * The first time the input changes, it sets the hasChanges flag to true.
   * @param value The new coordinates input value
   */
  function updateCoordinatesField(value: string): void {
    if (coordinatesError) {
      setCoordinatesError("");
    }
    if (!hasChanges) {
      setHasChanges(true);
    }
    setCoordinates(value);
  }

  /**
   * Handles a change in the address input field.
   * If the new value is a valid coordinates string, it sets the coordinates field to
   * that value, otherwise it leaves the coordinates field unchanged.
   * It also moves the map to the new coordinates.
   * @param input The new address input value
   */
  function onAddressInputChange(input: string): void {
    updateAddressField(input);
    const trimmedInput = input.trim();
    const position = parseCoordinatesString(trimmedInput);

    if (!position) {
      return;
    }

    updateCoordinatesField(trimmedInput);
    goToCoordinates(trimmedInput);
  }

  /**
   * Handles the selection of a geocoded address.
   * Sets both address and coordinates.
   * It also moves the map to the new coordinates.
   * @param geocodedAddress The selected geocoded address
   */
  function onGeocodedAddressSelect(geocodedAddress: GeocodedAddress): void {
    updateAddressField(geocodedAddress.address);
    const positionString = stringifyCoordinates(geocodedAddress.position);
    updateCoordinatesField(positionString);
    goToCoordinates(positionString);
  }

  /**
   * Handles clicks on the map that are not on a marker or cluster.
   * It extracts the GPS coordinates from the clicked position, then it reverse geocodes them
   * to get a human-readable address, and finally it sets both the address and coordinates fields.
   * @param event The map click event
   */
  async function onMapClick(event: __esri.ViewClickEvent): Promise<void> {
    const { mapPoint } = event;
    const lat = mapPoint.latitude;
    const long = mapPoint.longitude;

    try {
      if (
        typeof lat !== "number" ||
        isNaN(lat) ||
        typeof long !== "number" ||
        isNaN(long)
      ) {
        throw new Error("Failed to extract coordinates from map click event");
      }

      const address = await reverseGeocode({ lat, long });
      const positionString = stringifyCoordinates({ lat, long });
      updateAddressField(address);
      updateCoordinatesField(positionString);
    } catch (error) {
      handleErrorWithToast({
        id: `onMapClick-${Date.now().toString()}`,
        title: "Failed to set a location from a map click",
        error,
      });
    }
  }

  /** Issues the request to update the address and coordinates */
  async function onConfirm(): Promise<void> {
    const updatedAddress = address.trim() || DASH;
    let updatedPosition = undefined;

    // If the coordinates input is empty then assign null to the position to unset it in the backend.
    // If the coordinates input has an invalid value then set the error state and do an early exit.
    // If the coordinates input has a valid value then update the position with it.
    const sanitizedCoordinates = coordinates.trim();
    if (sanitizedCoordinates.length === 0) {
      updatedPosition = null;
    } else {
      const parsedCoordinates = parseCoordinatesString(sanitizedCoordinates);
      if (parsedCoordinates === null) {
        return setCoordinatesError("Invalid coordinates");
      } else {
        updatedPosition = parsedCoordinates;
      }
    }

    setIsUpdating(true);

    try {
      await dispatch(
        updateProjectDetails({
          projectId: project.id,
          payload: {
            address: updatedAddress,
            position: updatedPosition,
          },
        })
      ).unwrap();

      openToast({
        title: "Project location updated successfully.",
      });
      onClose();
    } catch (_) {
      // Do nothing as the error is already handled by the error slice.
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      onConfirm={onConfirm}
      isConfirmDisabled={!hasChanges}
      showSpinner={isUpdating}
      disabled={isUpdating || geocodingAddressState === "pending"}
      confirmText="Update"
      title="Update project location"
    >
      <Text variant="labelM" sx={{ mb: "8px" }}>
        Address
      </Text>
      <AddressAutocomplete
        input={address}
        setInput={onAddressInputChange}
        onGeocodedAddressSelect={onGeocodedAddressSelect}
        onLoadingGeocodedAddressChange={setGeocodingAddressState}
        sx={{ mb: "24px" }}
      />
      <Text variant="helpText" sx={{ mb: "8px" }}>
        You can also click on the map to set a location
      </Text>
      <WorldMap
        mapRef={mapRef}
        entities={entities}
        onMapClick={onMapClick}
        shouldShowMapControls
        sx={{
          height: "300px",
          marginBottom: "24px",
          cursor: "crosshair",
        }}
      />
      <TextField
        label={<Text variant="labelM">Coordinates</Text>}
        placeholder="Latitude, Longitude"
        fullWidth
        onTextChanged={updateCoordinatesField}
        error={coordinatesError}
        text={coordinates}
        endAdornment={
          coordinates.length > 0 ? (
            <IconButton
              size="s"
              margin={0.5}
              onClick={() => updateCoordinatesField("")}
            >
              <CloseIcon />
            </IconButton>
          ) : undefined
        }
      />
    </Dialog>
  );
}
